import type { CaseTrackingRow } from '../data/reports';
import type { ApiResult } from './types';
import { reportsApiRequest } from './apiClient';
import { cachedReportFetch, dedupeReportSearch } from './reportLookupCache';
import {
  downloadReportBlob,
  exportReportBlob,
  type ReportExportFile,
  extractReportLookupRows,
  extractReportSearchPagePayload,
  mapReportSearchPage,
  nullIfEmpty,
  nullIfEmptyNumericIds,
  toLabel,
  withReportDateRange,
  withReportTimeZone,
  type ReportSearchPage,
} from './reportApiCommon';

export type ReportExportFormat = 'PDF' | 'EXCEL';

export type CaseTrackingReportFilters = {
  fileCategoryIds: string[];
  subFileCategoryIds: string[];
  submittedAgencyIds: string[];
  taskStatusIds: string[];
  urgencyLevelIds: string[];
  workflowStageIds: string[];
  allocationOfficerIds: string[];
  assignedOfficerIds: string[];
  supervisorIds: string[];
  dateFrom: string;
  dateTo: string;
};

export type CaseTrackingLookupOption = {
  id: string;
  name: string;
};

type ApiLookupRow = {
  id?: string | number;
  name?: string;
  display_name?: string;
  displayName?: string;
};

type ApiSearchRow = {
  case_request_id?: string;
  id?: string;
  file_reference_number?: string;
  file_ref_no?: string | null;
  fileRef?: string;
  file_category?: string | null;
  file_category_name?: string;
  fileCategory?: string;
  sub_file_category?: string | null;
  sub_file_category_name?: string;
  subFileCategory?: string;
  submitted_agency?: string | null;
  submitted_agency_name?: string;
  submittedAgency?: string;
  submitted_at?: string | null;
  submittedAt?: string;
  workflow_stage?: string | null;
  workflow_stage_name?: string;
  workflowStage?: string;
  allocation_officer?: string | null;
  allocation_officer_name?: string;
  allocationOfficer?: string;
  assigned_officer?: string | null;
  assigned_officer_name?: string;
  assignedOfficer?: string;
  supervisor?: string | null;
  supervisor_name?: string;
  task_status?: string | null;
  task_status_name?: string;
  taskStatus?: string;
  urgency_level?: string | null;
  urgency_level_name?: string;
  urgencyLevel?: string;
};

function mapLookupRow(row: ApiLookupRow): CaseTrackingLookupOption | null {
  const id = row.id != null ? String(row.id).trim() : '';
  const name = (row.display_name ?? row.displayName ?? row.name ?? '').trim();
  if (!id || !name) return null;
  return { id, name };
}

/** Unique display names for dropdowns; API may return many ids per stage name. */
export function dedupeCaseTrackingLookupByName(options: CaseTrackingLookupOption[]): CaseTrackingLookupOption[] {
  const seen = new Map<string, CaseTrackingLookupOption>();
  for (const opt of options) {
    const key = opt.name.trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, opt);
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function mapCaseTrackingLookupNamesToIds(
  options: CaseTrackingLookupOption[],
  names: string[],
  matchAllIdsForName = false,
): string[] {
  const ids: string[] = [];
  for (const name of names) {
    const key = name.trim().toLowerCase();
    const matches = options.filter((o) => o.name.trim().toLowerCase() === key);
    if (matchAllIdsForName) {
      ids.push(...matches.map((o) => o.id));
    } else if (matches[0]) {
      ids.push(matches[0].id);
    }
  }
  return [...new Set(ids)];
}

async function listLookup(path: string): Promise<ApiResult<CaseTrackingLookupOption[]>> {
  return cachedReportFetch(`case-tracking:${path}`, async () => {
    const res = await reportsApiRequest<unknown>(path, { method: 'GET' });
    if (!res.ok) return { success: false, message: res.message };
    const mapped = extractReportLookupRows<ApiLookupRow>(res.data)
      .map(mapLookupRow)
      .filter((row): row is CaseTrackingLookupOption => row !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { success: true, data: mapped };
  }, {
    // Keep failed lookups retryable; successful (even empty) responses are cached.
    cacheWhen: (result) => result.success,
  });
}

export const listCaseTrackingAgencies = () => listLookup('/case-tracking/agencies');
export const listCaseTrackingTaskStatuses = () => listLookup('/case-tracking/task-statuses');
export const listCaseTrackingWorkflowStages = () => listLookup('/case-tracking/workflow-stages');
export const listCaseTrackingUsers = () => listLookup('/case-tracking/users');

function buildCaseTrackingFilterBody(filters: CaseTrackingReportFilters): Record<string, unknown> {
  return {
    category_ids: nullIfEmpty(filters.fileCategoryIds),
    sub_category_ids: nullIfEmpty(filters.subFileCategoryIds),
    agency_ids: nullIfEmptyNumericIds(filters.submittedAgencyIds),
    task_status_ids: nullIfEmpty(filters.taskStatusIds),
    urgency_level_ids: nullIfEmpty(filters.urgencyLevelIds),
    workflow_stage_ids: nullIfEmpty(filters.workflowStageIds),
    allocation_officer_ids: nullIfEmpty(filters.allocationOfficerIds),
    assigned_officer_ids: nullIfEmpty(filters.assignedOfficerIds),
    supervisor_ids: nullIfEmpty(filters.supervisorIds),
  };
}

function buildSearchBody(filters: CaseTrackingReportFilters): Record<string, unknown> {
  return withReportDateRange(buildCaseTrackingFilterBody(filters), filters.dateFrom, filters.dateTo);
}

function buildExportBody(filters: CaseTrackingReportFilters): Record<string, unknown> {
  return withReportTimeZone(
    withReportDateRange(
      buildCaseTrackingFilterBody(filters),
      filters.dateFrom,
      filters.dateTo,
      { from: 'date_from', to: 'date_to' },
    ),
  );
}

function displayCell(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  if (!t || t === '-') return '—';
  return t;
}

function mapSearchRow(row: ApiSearchRow, index: number): CaseTrackingRow {
  return {
    id: (row.case_request_id ?? row.id ?? `ctr-${index}`).trim(),
    fileRef: displayCell(row.file_reference_number ?? row.file_ref_no ?? row.fileRef),
    fileCategory: displayCell(row.file_category ?? row.file_category_name ?? row.fileCategory),
    subFileCategory: displayCell(row.sub_file_category ?? row.sub_file_category_name ?? row.subFileCategory),
    submittedAgency: displayCell(row.submitted_agency ?? row.submitted_agency_name ?? row.submittedAgency),
    submittedAt: (row.submitted_at ?? row.submittedAt ?? '').trim(),
    workflowStage: displayCell(row.workflow_stage ?? row.workflow_stage_name ?? row.workflowStage),
    allocationOfficer: displayCell(row.allocation_officer ?? row.allocation_officer_name ?? row.allocationOfficer),
    assignedOfficer: displayCell(row.assigned_officer ?? row.assigned_officer_name ?? row.assignedOfficer),
    supervisor: displayCell(row.supervisor ?? row.supervisor_name),
    taskStatus: displayCell(toLabel(row.task_status ?? row.task_status_name ?? row.taskStatus)),
    urgencyLevel: displayCell(row.urgency_level ?? row.urgency_level_name ?? row.urgencyLevel),
  };
}

export async function searchCaseTrackingReport(
  filters: CaseTrackingReportFilters,
  page: number,
  size: number,
): Promise<ApiResult<ReportSearchPage<CaseTrackingRow>>> {
  const body = buildSearchBody(filters);
  const searchKey = `case-tracking:search:${page}:${size}:${JSON.stringify(body)}`;

  return dedupeReportSearch(searchKey, async () => {
    const res = await reportsApiRequest<unknown>(`/case-tracking/search?page=${page}&size=${size}`, {
      method: 'POST',
      body,
    });
    if (!res.ok) return { success: false, message: res.message };

    const pagePayload = extractReportSearchPagePayload<ApiSearchRow>(res.data);
    const content = (pagePayload?.content ?? []).map(mapSearchRow);
    const mapped = mapReportSearchPage({ ...pagePayload, content }, page, size);
    return { success: true, data: mapped };
  });
}

export async function exportCaseTrackingReport(
  format: ReportExportFormat,
  filters: CaseTrackingReportFilters,
): Promise<ApiResult<ReportExportFile>> {
  return exportReportBlob(
    `/case-tracking/export?format=${encodeURIComponent(format)}`,
    buildExportBody(filters),
  );
}

export { downloadReportBlob };
